import { Paper, Text, Button, Container } from "@mantine/core";
import { useForm } from "react-hook-form";
import { Textarea, TextInput } from "react-hook-form-mantine";
import { useGetSpeakerById, usePatchSpeaker } from "../../api/speakers";
import { useParams } from "react-router-dom";
import { extractDirtyData } from "../../utils/dirtyData";
import ImageUpload from "../ImageUpload";
import ConnectToUser from "./ConnectToUser";
import { web_patchSpeakerSchema } from "@congrs/schemas";
import resolver from "@congrs/resolver";
import ExternalLinksInput from "../ExternalLinksInput";

function EditSpeakerForm({ speaker }) {
  const {
    firstName,
    lastName,
    occupation,
    biography,
    id,
    picture,
    userId,
    links,
  } = speaker;

  const defaultValues = {
    firstName,
    lastName,
    occupation,
    biography,
    picture: picture ?? "",
    userId,
    links,
  };

  const {
    control,
    setValue,
    handleSubmit,
    reset,
    formState: { isDirty, dirtyFields },
  } = useForm({
    defaultValues,
    resolver: resolver(web_patchSpeakerSchema),
  });

  const { mutate, isPending } = usePatchSpeaker(id);

  const onSubmit = (data) => {
    const dirtyData = extractDirtyData(data, dirtyFields);

    mutate(dirtyData, {
      onSuccess: () => {
        reset({ ...defaultValues, ...dirtyData });
      },
    });
  };

  const handleUserIdUpdate = (userId) => {
    mutate({ userId }, { onSuccess: () => setValue("userId", userId) });
  };

  return (
    <Container w={600}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Paper withBorder shadow="sm" px="sm" py="md" style={{ marginTop: 20 }}>
          <ImageUpload
            control={control}
            name="picture"
            label="Profile picture"
            defaultImage={picture}
            resize
          />

          <TextInput
            label="First Name"
            placeholder="John"
            name="firstName"
            control={control}
            mt="sm"
            withAsterisk
          />
          <TextInput
            label="Last Name"
            placeholder="Doe"
            name="lastName"
            control={control}
            withAsterisk
            mt="sm"
          />
          <TextInput
            label="Occupation"
            placeholder="Software Engineer at RoundCube"
            name="occupation"
            control={control}
            mt="sm"
          />
          <Textarea
            label="Biography"
            placeholder="Tell us about the speaker"
            name="biography"
            control={control}
            autosize
            minRows={3}
            mt="sm"
          />

          <ExternalLinksInput
            control={control}
            setValue={setValue}
            name="links"
            mt="md"
          />

          <Button
            type="submit"
            color="blue"
            style={{ marginTop: 20 }}
            loading={isPending}
            disabled={!isDirty}
          >
            Edit speaker
          </Button>
        </Paper>
      </form>
      <ConnectToUser
        speakerId={id}
        onUpdate={handleUserIdUpdate}
        userId={userId}
      />
    </Container>
  );
}

const EditSpeaker = () => {
  const { speakerId } = useParams();
  const {
    data: speaker,
    isPending: speakerLoading,
    isSuccess,
    isError,
    error,
  } = useGetSpeakerById(speakerId);

  if (speakerLoading) {
    return <Text>Loading...</Text>;
  }

  if (isError) {
    return <Text c="red">{error.message}</Text>;
  }

  if (isSuccess) {
    return <EditSpeakerForm speaker={speaker} />;
  }
};

export default EditSpeaker;
